/**
 * Utilidades de validación para formularios
 */

/**
 * Verifica si un campo está vacío
 * @param {string} value - Valor a verificar
 * @param {string} [message="Este campo es obligatorio"] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateRequired = (value, message = "Este campo es obligatorio") => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return message;
  }
  return null;
};

/**
 * Valida el formato de un correo electrónico
 * @param {string} email - Correo electrónico a validar
 * @param {string} [message="Formato de correo electrónico inválido"] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateEmail = (email, message = "Formato de correo electrónico inválido") => {
  if (!email) return null; // Si está vacío, la validación required se encargará
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return message;
  }
  return null;
};

/**
 * Valida la longitud mínima de un texto
 * @param {string} value - Valor a validar
 * @param {number} min - Longitud mínima requerida
 * @param {string} [message] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateMinLength = (value, min, message) => {
  if (!value) return null; // Si está vacío, la validación required se encargará
  
  if (value.length < min) {
    return message || `Debe contener al menos ${min} caracteres`;
  }
  return null;
};

/**
 * Valida la longitud máxima de un texto
 * @param {string} value - Valor a validar
 * @param {number} max - Longitud máxima permitida
 * @param {string} [message] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateMaxLength = (value, max, message) => {
  if (!value) return null; // Si está vacío, la validación required se encargará
  
  if (value.length > max) {
    return message || `Debe contener máximo ${max} caracteres`;
  }
  return null;
};

/**
 * Valida que un valor sea numérico
 * @param {string|number} value - Valor a validar
 * @param {string} [message="Debe ser un valor numérico"] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateNumeric = (value, message = "Debe ser un valor numérico") => {
  if (!value) return null; // Si está vacío, la validación required se encargará
  
  if (isNaN(Number(value))) {
    return message;
  }
  return null;
};

/**
 * Valida que un valor contenga solo texto (letras y espacios)
 * @param {string} value - Valor a validar
 * @param {string} [message="El campo debe contener solo letras y espacios"] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateString = (value, message = "El campo debe contener solo letras y espacios") => {
  if (!value) return null; // Si está vacío, la validación required se encargará
  
  // Expresión regular para validar solo letras (incluyendo acentos y ñ) y espacios
  const onlyLettersRegex = /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]+$/;
  
  if (!onlyLettersRegex.test(value)) {
    return message;
  }
  return null;
};

/**
 * Valida un rango numérico
 * @param {string|number} value - Valor a validar
 * @param {number} min - Valor mínimo
 * @param {number} max - Valor máximo
 * @param {string} [message] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateNumberRange = (value, min, max, message) => {
  if (!value) return null; // Si está vacío, la validación required se encargará
  if (isNaN(Number(value))) return "Debe ser un valor numérico";
  
  const numValue = Number(value);
  if (numValue < min || numValue > max) {
    return message || `Debe ser un número entre ${min} y ${max}`;
  }
  return null;
};

/**
 * Valida que un texto tenga una longitud exacta
 * @param {string} value - Valor a validar
 * @param {number} length - Longitud exacta requerida
 * @param {string} [message] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateExactLength = (value, length, message) => {
  if (!value) return null; // Si está vacío, la validación required se encargará
  
  if (value.length !== length) {
    return message || `Debe contener exactamente ${length} caracteres`;
  }
  return null;
};

/**
 * Valida una contraseña fuerte (al menos una mayúscula, una minúscula, un número y un caracter especial)
 * @param {string} password - Contraseña a validar
 * @param {string} [message="La contraseña debe contener al menos una mayúscula, una minúscula, un número y un caracter especial"] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateStrongPassword = (password, message = "La contraseña debe contener al menos una mayúscula, una minúscula, un número y un caracter especial") => {
  if (!password) return null; // Si está vacío, la validación required se encargará
  
  // Al menos una mayúscula, una minúscula, un número y un caracter especial
  const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
  if (!strongPasswordRegex.test(password)) {
    return message;
  }
  return null;
};

/**
 * Valida que dos valores sean iguales (ej. confirmación de contraseña)
 * @param {string} value - Valor a validar
 * @param {string} compareValue - Valor con el que comparar
 * @param {string} [message="Los valores no coinciden"] - Mensaje de error personalizado
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateMatch = (value, compareValue, message = "Los valores no coinciden") => {
  if (!value) return null; // Si está vacío, la validación required se encargará
  
  if (value !== compareValue) {
    return message;
  }
  return null;
};

/**
 * Ejecuta múltiples validaciones y devuelve el primer error encontrado
 * @param {string} value - Valor a validar
 * @param {Array<Function>} validations - Array de funciones de validación a ejecutar
 * @returns {string|null} Primer mensaje de error encontrado o null si todas las validaciones pasan
 */
export const validateAll = (value, validations) => {
  for (const validation of validations) {
    const error = validation(value);
    if (error) {
      return error;
    }
  }
  return null;
};

/**
 * Valida un formulario completo
 * @param {Object} values - Objeto con los valores del formulario
 * @param {Object} validationRules - Objeto con las reglas de validación para cada campo
 * @returns {Object} Objeto con los errores encontrados
 */
export const validateForm = (values, validationRules) => {
  const errors = {};
  
  Object.keys(validationRules).forEach(field => {
    const fieldValidations = validationRules[field];
    const value = values[field];
    
    const error = validateAll(value, fieldValidations);
    if (error) {
      errors[field] = error;
    }
  });
  
  return errors;
};  

/**
 * Valida que se haya seleccionado al menos un proyecto
 * @param {Array} value - Array de proyectos seleccionados
 * @returns {string|null} Mensaje de error o null si es válido
 */
export const validateProjectsClient = (value) => {
  console.log(value.length);
  if (!value) {
    return "Este campo es obligatorio";
  }

  if (value.length === 0) {
    return "Debe seleccionar al menos un proyecto";
  }
  return null;
};